import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as z from 'zod'
import { LuCircleCheckBig } from 'react-icons/lu'

import { FormattedMessage } from 'react-intl'

import { FeatureModal, FeatureModalProps } from '@/lib/core/components/Modal'
import { TextField } from '@/lib/core/components/TextField'
import { Button } from '@/lib/core/components/Button'
import { formsMessages } from '@/lib/core/messages'

import { useCreateInvitation } from '@/lib/features/Contacts/api'
import messages from '@/lib/features/Contacts/contacts.messages'
import { ContactSuccessModal } from '@/lib/features/Contacts/components/ContactSuccessModal'
import {
  AddContactActions,
  AddContactContent,
  AddContactFieldRow,
} from '@/lib/features/Contacts/components/AddContactModal/AddContactModal.styles'
import { FormLegend } from '@/lib/features/Assets/components/AddAssetModal/AddAssetModal.styles'
import { CreateMyInvitationPayload } from '@/lib/features/Contacts/interfaces'
import { PTUResponseError } from '@/lib/features/Assets/interfaces'

import { FeatureTypes } from '@/lib/utils/features'
import { setFormErrors } from '@/lib/utils/forms'

export type AddContactModalValues = CreateMyInvitationPayload
export type AddContactModalProps = Pick<FeatureModalProps, 'isActive' | 'onClose'> & {
  onSuccess?: (value: string) => void
}
const validationSchema = z.object({
  organizationToIdOrEmail: z
    .string({
      required_error: formsMessages.requiredValidation.defaultMessage,
    })
    .email(formsMessages.emailOrNumberId.defaultMessage)
    .or(
      z
        .string()
        .refine(
          (value) => !Number.isNaN(Number(value)),
          formsMessages.emailOrNumberId.defaultMessage,
        ),
    ),
})

export const AddContactModal = ({
  isActive,
  onClose = () => null,
  onSuccess = () => null,
}: AddContactModalProps) => {
  const formMethods = useForm<AddContactModalValues>({
    resolver: zodResolver(validationSchema),
  })
  const { handleSubmit, formState, setError, reset } = formMethods
  const [successUserInput, setSuccessUserInput] = useState('')
  const { trigger: createMyInvitation } = useCreateInvitation()

  const onSubmit = async (values: AddContactModalValues) => {
    try {
      // Normalize email if the input is an email address (not a numeric ID)
      const normalizedValues = {
        ...values,
        organizationToIdOrEmail: Number.isNaN(Number(values.organizationToIdOrEmail))
          ? values.organizationToIdOrEmail.toLowerCase().trim()
          : values.organizationToIdOrEmail,
      }

      await createMyInvitation(normalizedValues)
      setSuccessUserInput(normalizedValues.organizationToIdOrEmail)
      onSuccess(normalizedValues.organizationToIdOrEmail)
    } catch (err) {
      const errors = err as PTUResponseError

      if (errors) {
        setFormErrors(setError, errors)
      }
    }
  }

  useEffect(() => {
    if (!isActive) {
      reset()
      setSuccessUserInput('')
    }

    return () => {
      reset()
      setSuccessUserInput('')
    }
  }, [isActive])

  if (successUserInput) {
    return <ContactSuccessModal userInput={successUserInput} onClose={() => onClose(true)} />
  }

  return (
    <FeatureModal
      feature={FeatureTypes.CONTACTS}
      isActive={isActive}
      title={messages.addNewHeading.defaultMessage}
      onClose={() => onClose()}
      titleIcon={<LuCircleCheckBig size="40" />}
      subtitle={messages.addNewSubtitle.defaultMessage}
    >
      <AddContactContent>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" noValidate>
            <FormLegend>
              <FormattedMessage {...messages.required} />
            </FormLegend>
            <AddContactFieldRow>
              <TextField
                name="organizationToIdOrEmail"
                label={messages.addNewFieldLabel.defaultMessage}
                required
                displayInline
                disabled={formState.isSubmitting}
                placeholder={messages.addNewFieldPlaceholder.defaultMessage}
              />
            </AddContactFieldRow>
            <AddContactActions>
              <Button
                type="submit"
                variant="secondary"
                size="large"
                $fitToContent
                minWidth
                disabled={formState.isSubmitting}
              >
                <FormattedMessage {...messages.addNewSubmit} />
              </Button>
            </AddContactActions>
          </form>
        </FormProvider>
      </AddContactContent>
    </FeatureModal>
  )
}
